import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { FileUpload } from "@/components/FileUpload";
import { NoteSpesePanel } from "@/pages/personale/NoteSpesePanel";
import { HRDashboard } from "@/pages/personale/HRDashboard";
import { GiornaliereCalendar } from "@/pages/personale/GiornaliereCalendar";
import { ManualeButton } from "@/components/ManualeButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Pencil, Trash2, ArrowLeft, Banknote, PiggyBank, CalendarDays, KeyRound, FolderOpen, Upload, Download, ShieldCheck, FileText, Loader2, Wallet, TrendingUp } from "lucide-react";

const RUOLI = ["Socio Intermediario", "Collaboratore", "Amministrativo", "Responsabile", "Titolare"];
const SEDI = ["Macomer", "Bonorva", "Altro"];
const DOC_CATEGORIE = ["Busta paga", "Contratto", "CU/CUD", "Documento identità", "Codice fiscale", "IBAN", "Certificato", "Attestato/Corso", "Comunicazione", "Altro"];
const TIPI_RICHIESTA = ["Ferie", "Permesso/ROL", "Malattia", "Infortunio", "Legge 104", "Lutto", "Congedo", "Smartworking", "Trasferta", "Altro"];
const STATO_BADGE = {
  in_attesa: "bg-amber-100 text-amber-700", approvata: "bg-emerald-100 text-emerald-700",
  respinta: "bg-red-100 text-red-600", annullata: "bg-slate-100 text-slate-500",
};
const STATO_LABEL = { in_attesa: "In attesa", approvata: "Approvata", respinta: "Respinta", annullata: "Annullata" };
const euro = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Personale() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [persone, setPersone] = useState([]);
  const loadPersone = () => api.get("/personale/persone").then((r) => setPersone(r.data)).catch(() => setPersone([]));
  useEffect(() => {
    api.get("/personale/me").then((r) => {
      setMe(r.data);
      if (r.data.is_manager) loadPersone();
    }).catch(() => setMe({ is_manager: false, persona: null }));
  }, []);
  const nomeOf = (id) => persone.find((p) => p.id === id)?.nome || "—";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="pers-back-btn" onClick={() => navigate("/gestione-agenzia")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Gestione Agenzia</button>
        <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><Users className="h-6 w-6" /></div>
            <div>
              <h1 className="font-heading text-3xl font-medium text-slate-900">{me && !me.is_manager ? "La mia area" : "Gestione del Personale"}</h1>
              <p className="text-slate-500 text-sm">{me && !me.is_manager ? "I tuoi dati, documenti e buste paga." : "Soci e collaboratori, stipendi, TFR/PIP e giornaliere."}</p>
            </div>
          </div>
          <ManualeButton route="/manuale/personale" />
        </div>

        {!me ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !me.is_manager ? (
          <MyArea me={me} />
        ) : (
          <Tabs defaultValue="cruscotto">
            <TabsList data-testid="pers-tabs">
              <TabsTrigger value="cruscotto" data-testid="tab-cruscotto"><TrendingUp className="h-4 w-4 mr-1.5" />Cruscotto</TabsTrigger>
              <TabsTrigger value="persone" data-testid="tab-persone"><Users className="h-4 w-4 mr-1.5" />Soci/Collaboratori</TabsTrigger>
              <TabsTrigger value="assenze" data-testid="tab-assenze"><CalendarDays className="h-4 w-4 mr-1.5" />Assenze</TabsTrigger>
              <TabsTrigger value="note-spese" data-testid="tab-note-spese"><Wallet className="h-4 w-4 mr-1.5" />Note spese</TabsTrigger>
              <TabsTrigger value="stipendi" data-testid="tab-stipendi"><Banknote className="h-4 w-4 mr-1.5" />Stipendi</TabsTrigger>
              <TabsTrigger value="tfr" data-testid="tab-tfr"><PiggyBank className="h-4 w-4 mr-1.5" />TFR / PIP</TabsTrigger>
              <TabsTrigger value="giornaliere" data-testid="tab-giornaliere"><CalendarDays className="h-4 w-4 mr-1.5" />Giornaliere</TabsTrigger>
              {me.persona && <TabsTrigger value="mie-giornaliere" data-testid="tab-mie-giornaliere"><CalendarDays className="h-4 w-4 mr-1.5" />Le mie giornaliere</TabsTrigger>}
            </TabsList>

            <TabsContent value="cruscotto" className="mt-6"><HRDashboard /></TabsContent>
            <TabsContent value="persone" className="mt-6"><PersoneTab persone={persone} reload={loadPersone} isAdmin={me.is_admin} /></TabsContent>
            <TabsContent value="assenze" className="mt-6"><RichiestePanel manager={true} persone={persone} /></TabsContent>
            <TabsContent value="note-spese" className="mt-6"><NoteSpesePanel manager={true} persone={persone} /></TabsContent>
            <TabsContent value="stipendi" className="mt-6"><StipendiTab persone={persone} nomeOf={nomeOf} /></TabsContent>
            <TabsContent value="tfr" className="mt-6"><TfrTab persone={persone} nomeOf={nomeOf} /></TabsContent>
            <TabsContent value="giornaliere" className="mt-6">
              {me.is_admin && <PermessiGeneralePanel />}
              <GiornaliereCalendar persone={persone} />
            </TabsContent>
            {me.persona && <TabsContent value="mie-giornaliere" className="mt-6"><GiornaliereCalendar selfMode /></TabsContent>}
          </Tabs>
        )}
      </main>
    </div>
  );
}

// ---- Fascicolo documenti (condiviso: area dipendente e gestore) ----
function Fascicolo({ personaId, canManage }) {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("Altro");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/personale/documenti", { params: personaId ? { persona_id: personaId } : {} }).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, [personaId]); // eslint-disable-line
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("persona_id", personaId); fd.append("categoria", cat);
      await api.post("/personale/documenti", fd);
      toast.success("Documento caricato"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/personale/documenti/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename || "documento"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };
  const del = async (d) => { if (!window.confirm("Eliminare il documento?")) return; try { await api.delete(`/personale/documenti/${d.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <div><Label className="text-xs">Categoria</Label><Select value={cat} onValueChange={setCat}><SelectTrigger className="w-52" data-testid="doc-categoria"><SelectValue /></SelectTrigger><SelectContent>{DOC_CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <label className="inline-flex">
          <input type="file" className="hidden" onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" data-testid="doc-upload-input" disabled={busy} />
          <span className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm cursor-pointer ${busy ? "bg-slate-100 text-slate-400" : "brand-gradient text-white"}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica documento</span>
        </label>
      </div>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Categoria</TableHead><TableHead>File</TableHead><TableHead>Origine</TableHead><TableHead>Data</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                <TableCell className="text-sm">{d.categoria}</TableCell>
                <TableCell className="text-sm flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-slate-400" />{d.original_filename}</TableCell>
                <TableCell><span className={`text-xs ${d.origine === "ufficio" ? "text-blue-600" : "text-slate-500"}`}>{d.origine === "ufficio" ? "Ufficio" : "Dipendente"}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{(d.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => download(d)} className="text-slate-400 hover:text-brand-blue mr-3" data-testid={`doc-dl-${d.id}`}><Download className="h-4 w-4" /></button>
                  {(canManage || d.origine === "dipendente") && <button onClick={() => del(d)} className="text-slate-400 hover:text-red-500" data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></button>}
                </TableCell>
              </TableRow>
            ))}
            {docs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6 text-sm">Nessun documento.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---- Area personale del dipendente ----
function MyArea({ me }) {
  const p = me.persona;
  const [stipendi, setStipendi] = useState([]);
  useEffect(() => { api.get("/personale/me/stipendi").then((r) => setStipendi(r.data)).catch(() => setStipendi([])); }, []);
  if (!p) return <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-800 text-sm" data-testid="myarea-noprofile">Il tuo account non è ancora collegato a una scheda personale. Contatta l'ufficio.</div>;
  return (
    <div className="space-y-6" data-testid="myarea">
      <div className="rounded-xl border border-slate-200 bg-white p-5 grid sm:grid-cols-4 gap-4">
        <Info label="Nome" value={p.nome} />
        <Info label="Ruolo" value={p.ruolo} />
        <Info label="Sede" value={p.sede} />
        <Info label="Email" value={p.email || "—"} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-indigo-600" /> Le mie richieste (ferie, permessi, malattia…)</h3>
        <RichiestePanel manager={false} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" /> Le mie note spese / trasferte</h3>
        <NoteSpesePanel manager={false} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-blue-600" /> Le mie giornaliere (calendario attività, trasferte, rimborsi)</h3>
        <GiornaliereCalendar selfMode />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><FolderOpen className="h-5 w-5 text-blue-600" /> I miei documenti</h3>
        <Fascicolo personaId={p.id} canManage={false} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><Banknote className="h-5 w-5 text-emerald-600" /> Le mie buste paga</h3>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Mese</TableHead><TableHead>Importo</TableHead><TableHead>Stato</TableHead><TableHead>Documento</TableHead></TableRow></TableHeader>
            <TableBody>
              {stipendi.map((s) => (
                <TableRow key={s.id} data-testid={`my-stip-${s.id}`}>
                  <TableCell>{s.mese}</TableCell>
                  <TableCell>{euro(s.importo)}</TableCell>
                  <TableCell><span className={s.stato === "pagato" ? "text-green-600 text-xs" : "text-orange-600 text-xs"}>{s.stato === "pagato" ? "Pagato" : "Da pagare"}</span></TableCell>
                  <TableCell className="w-40"><FileUpload modulo="busta_paga" refId={s.id} compact readOnly testid={`my-bp-${s.id}`} /></TableCell>
                </TableRow>
              ))}
              {stipendi.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6 text-sm">Nessuna busta paga disponibile.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (<div><div className="text-xs text-slate-400">{label}</div><div className="text-sm font-medium text-slate-800">{value}</div></div>);

// ---- Fase 2: Richieste assenze (dipendente + gestore) ----
function RichiestePanel({ manager, persone = [] }) {
  const [rows, setRows] = useState([]);
  const [filtro, setFiltro] = useState("tutte");
  const [riep, setRiep] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [decDlg, setDecDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const nomeOf = (id) => persone.find((p) => p.id === id)?.nome || "—";

  const load = () => {
    const params = manager && filtro !== "tutte" ? { stato: filtro } : {};
    api.get("/personale/richieste", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [filtro]); // eslint-disable-line
  useEffect(() => { if (!manager) api.get("/personale/richieste/riepilogo").then((r) => setRiep(r.data)).catch(() => {}); }, [rows.length]); // eslint-disable-line

  const emptyReq = { persona_id: "", tipo: "Ferie", data_inizio: "", data_fine: "", ore: 0, motivo: "", file: null };
  const create = async () => {
    if (manager && !dlg.persona_id) return toast.error("Seleziona la persona");
    if (!dlg.data_inizio || !dlg.data_fine) return toast.error("Indica le date");
    setBusy(true);
    try {
      const { data } = await api.post("/personale/richieste", {
        persona_id: manager ? dlg.persona_id : undefined, tipo: dlg.tipo,
        data_inizio: dlg.data_inizio, data_fine: dlg.data_fine, ore: Number(dlg.ore) || 0, motivo: dlg.motivo,
      });
      if (dlg.file) { const fd = new FormData(); fd.append("file", dlg.file); await api.post(`/personale/richieste/${data.id}/allegato`, fd); }
      toast.success("Richiesta inviata"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const decidi = async (esito) => {
    setBusy(true);
    try { await api.post(`/personale/richieste/${decDlg.id}/decisione`, { esito, nota: decDlg.nota || "" }); toast.success(esito === "approvata" ? "Approvata" : "Respinta"); setDecDlg(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const approva = async (r) => {
    if (!window.confirm(`Approvare ${r.tipo} di ${r.persona_nome}?`)) return;
    try { await api.post(`/personale/richieste/${r.id}/decisione`, { esito: "approvata", nota: "" }); toast.success("Approvata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const annulla = async (r) => { if (!window.confirm("Annullare la richiesta?")) return; try { await api.delete(`/personale/richieste/${r.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  return (
    <div className="space-y-4" data-testid="richieste-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {manager ? (
          <Select value={filtro} onValueChange={setFiltro}><SelectTrigger className="w-48" data-testid="richieste-filtro"><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="tutte">Tutte</SelectItem><SelectItem value="in_attesa">In attesa</SelectItem>
            <SelectItem value="approvata">Approvate</SelectItem><SelectItem value="respinta">Respinte</SelectItem><SelectItem value="annullata">Annullate</SelectItem>
          </SelectContent></Select>
        ) : <div />}
        <Button data-testid="richieste-add" onClick={() => setDlg({ ...emptyReq })}><Plus className="h-4 w-4 mr-1.5" />Nuova richiesta</Button>
      </div>

      {!manager && riep && Object.keys(riep.per_tipo || {}).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="richieste-riepilogo">
          {Object.entries(riep.per_tipo).map(([t, v]) => (
            <div key={t} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs">
              <span className="text-slate-500">{t} {riep.anno}:</span> <b>{v.giorni} gg</b>{v.ore ? ` · ${v.ore} h` : ""}
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {manager && <TableHead>Persona</TableHead>}<TableHead>Tipo</TableHead><TableHead>Dal</TableHead><TableHead>Al</TableHead>
            <TableHead>Stato</TableHead><TableHead>Approvato da</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`richiesta-row-${r.id}`}>
                {manager && <TableCell className="font-medium">{r.persona_nome || nomeOf(r.persona_id)}</TableCell>}
                <TableCell className="text-sm">{r.tipo}{r.motivo ? <span className="block text-xs text-slate-400">{r.motivo}</span> : null}</TableCell>
                <TableCell className="text-sm">{r.data_inizio}</TableCell>
                <TableCell className="text-sm">{r.data_fine}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${STATO_BADGE[r.stato]}`} data-testid={`richiesta-stato-${r.id}`}>{STATO_LABEL[r.stato]}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{r.approvatore_nome || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {r.allegato_name && <button onClick={async () => { const res = await api.get(`/personale/richieste/${r.id}/allegato`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); window.open(u); }} className="text-slate-400 hover:text-brand-blue mr-3" title="Allegato"><FileText className="h-4 w-4 inline" /></button>}
                  {manager && r.stato === "in_attesa" && <>
                    <button onClick={() => approva(r)} className="text-emerald-600 hover:text-emerald-700 mr-2 text-xs font-medium" data-testid={`richiesta-approva-${r.id}`}>Approva</button>
                    <button onClick={() => setDecDlg({ id: r.id, nota: "", reject: true, label: `${r.persona_nome} · ${r.tipo}` })} className="text-red-500 hover:text-red-600 mr-2 text-xs font-medium" data-testid={`richiesta-respingi-${r.id}`}>Respingi</button>
                  </>}
                  {!manager && r.stato === "in_attesa" && <button onClick={() => annulla(r)} className="text-slate-400 hover:text-red-500" data-testid={`richiesta-annulla-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={manager ? 7 : 6} className="text-center text-slate-400 py-6 text-sm">Nessuna richiesta.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Nuova richiesta */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="richiesta-dialog">
          <DialogHeader><DialogTitle>Nuova richiesta</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {manager && <div><Label>Persona</Label><Select value={dlg.persona_id} onValueChange={(v) => setDlg({ ...dlg, persona_id: v })}><SelectTrigger data-testid="richiesta-persona"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent></Select></div>}
              <div><Label>Tipo</Label><Select value={dlg.tipo} onValueChange={(v) => setDlg({ ...dlg, tipo: v })}><SelectTrigger data-testid="richiesta-tipo"><SelectValue /></SelectTrigger><SelectContent>{TIPI_RICHIESTA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Dal</Label><Input type="date" value={dlg.data_inizio} onChange={(e) => setDlg({ ...dlg, data_inizio: e.target.value })} data-testid="richiesta-dal" /></div>
                <div><Label>Al</Label><Input type="date" value={dlg.data_fine} onChange={(e) => setDlg({ ...dlg, data_fine: e.target.value })} data-testid="richiesta-al" /></div>
              </div>
              {(dlg.tipo === "Permesso/ROL") && <div><Label>Ore (opzionale)</Label><Input type="number" value={dlg.ore} onChange={(e) => setDlg({ ...dlg, ore: e.target.value })} /></div>}
              <div><Label>Motivo / note</Label><Textarea value={dlg.motivo} onChange={(e) => setDlg({ ...dlg, motivo: e.target.value })} /></div>
              <div><Label className="text-xs">Allegato (es. certificato) — opzionale</Label><input type="file" onChange={(e) => setDlg({ ...dlg, file: e.target.files?.[0] || null })} className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100" data-testid="richiesta-file" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={create} disabled={busy} data-testid="richiesta-invia">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia richiesta"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respingi con nota */}
      <Dialog open={!!decDlg} onOpenChange={(o) => !o && setDecDlg(null)}>
        <DialogContent data-testid="decisione-dialog">
          <DialogHeader><DialogTitle>Respingi richiesta</DialogTitle></DialogHeader>
          {decDlg && <div className="space-y-3"><p className="text-sm text-slate-500">{decDlg.label}</p><div><Label>Motivazione (opzionale)</Label><Textarea value={decDlg.nota} onChange={(e) => setDecDlg({ ...decDlg, nota: e.target.value })} data-testid="decisione-nota" /></div></div>}
          <DialogFooter><Button variant="outline" onClick={() => setDecDlg(null)}>Annulla</Button><Button className="bg-red-600 hover:bg-red-700 text-white border-0" onClick={() => decidi("respinta")} disabled={busy} data-testid="decisione-conferma">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Respingi"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermessiGeneralePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const load = () => { setLoading(true); api.get("/personale/permessi-generale").then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const LABELS = { nessuno: "Nessun accesso", visione: "Sola visione", delegato: "Delegato (vede e gestisce)" };
  const setLivello = async (row, livello) => {
    if (livello === row.livello) return;
    setSaving(row.id);
    try {
      await api.put(`/personale/persone/${row.id}/permesso-generale`, { livello });
      setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, livello } : x)));
      toast.success(livello === "nessuno" ? `Accesso al Generale revocato a ${row.nome}` : `${row.nome}: ${LABELS[livello]}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(null); }
  };
  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-white p-5" data-testid="permessi-generale-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-blue-600" />
        <h3 className="font-heading text-lg">Permessi Calendario Generale</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Scegli il livello di accesso al calendario di tutti i collaboratori: <b>Sola visione</b> (vede ma non modifica) oppure <b>Delegato</b> (vede, modifica, valida). L'Admin ha sempre accesso e non è modificabile.</p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="h-4 w-4 animate-spin" /> Carico…</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`permesso-row-${r.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{r.nome} {r.is_admin && <span className="ml-1 text-[10px] font-semibold text-purple-700 bg-purple-50 rounded px-1.5 py-0.5">Admin</span>}</div>
                <div className="text-xs text-slate-400 truncate">{r.email || r.ruolo}</div>
              </div>
              <Select value={r.livello} disabled={r.is_admin || saving === r.id} onValueChange={(v) => setLivello(r, v)}>
                <SelectTrigger className="w-[210px] h-9 text-sm" data-testid={`permesso-select-${r.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nessuno" data-testid={`permesso-opt-nessuno-${r.id}`}>Nessun accesso</SelectItem>
                  <SelectItem value="visione" data-testid={`permesso-opt-visione-${r.id}`}>Sola visione</SelectItem>
                  <SelectItem value="delegato" data-testid={`permesso-opt-delegato-${r.id}`}>Delegato (vede e gestisce)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
          {rows.length === 0 && <div className="text-sm text-slate-400 py-4">Nessun collaboratore con account collegato.</div>}
        </div>
      )}
    </div>
  );
}


function PersoneTab({ persone, reload, isAdmin }) {
  const [dlg, setDlg] = useState(null);
  const [accountDlg, setAccountDlg] = useState(null);
  const [fascPersona, setFascPersona] = useState(null);
  const empty = { nome: "", ruolo: "Collaboratore", sede: "Macomer", rui_number: "", email: "", telefono: "", attivo: true, is_approver: false, vede_calendario: false, note: "" };
  const save = async () => {
    if (!dlg.nome?.trim()) return toast.error("Nome obbligatorio");
    try {
      if (dlg.id) await api.put(`/personale/persone/${dlg.id}`, dlg);
      else await api.post("/personale/persone", dlg);
      toast.success("Salvato"); setDlg(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (p) => { if (!window.confirm(`Eliminare ${p.nome}?`)) return; await api.delete(`/personale/persone/${p.id}`); reload(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="pers-add-persona" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuova persona</Button></div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Ruolo</TableHead><TableHead>Sede</TableHead><TableHead>Giornaliera</TableHead><TableHead>Accesso</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
          <TableBody>
            {persone.map((p) => (
              <TableRow key={p.id} data-testid={`persona-row-${p.id}`}>
                <TableCell className="font-medium">{p.nome}</TableCell>
                <TableCell>{p.ruolo}</TableCell>
                <TableCell>{p.sede}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 items-start">
                    {p.user_id
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5" data-testid={`persona-gio-personale-${p.id}`}><CalendarDays className="h-3 w-3" />Personale</span>
                      : <span className="text-[11px] text-slate-400" data-testid={`persona-gio-none-${p.id}`}>No giornaliera</span>}
                    {(p.is_approver || p.vede_calendario) &&
                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 ${p.is_approver ? "text-blue-700 bg-blue-50" : "text-indigo-700 bg-indigo-50"}`} data-testid={`persona-gio-generale-${p.id}`}><Users className="h-3 w-3" />Vede Generale{p.is_approver ? " (delegato)" : " (solo visione)"}</span>}
                  </div>
                </TableCell>
                <TableCell>
                  {p.user_id ? <span className="inline-flex items-center gap-1 text-xs text-green-600">{p.is_approver ? <ShieldCheck className="h-3.5 w-3.5" /> : <KeyRound className="h-3.5 w-3.5" />}{p.is_approver ? "Delegato" : "Attivo"}</span> : <span className="text-xs text-slate-400">Nessuno</span>}
                </TableCell>
                <TableCell>{p.attivo ? <span className="text-green-600 text-xs">Attivo</span> : <span className="text-slate-400 text-xs">Inattivo</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setFascPersona(p)} className="text-slate-400 hover:text-brand-blue mr-3" title="Fascicolo documenti" data-testid={`persona-fasc-${p.id}`}><FolderOpen className="h-4 w-4" /></button>
                  <button onClick={() => setAccountDlg({ persona: p, email: p.email || "", password: "", is_approver: !!p.is_approver })} className="text-slate-400 hover:text-brand-blue mr-3" title="Gestisci accesso" data-testid={`persona-account-${p.id}`}><KeyRound className="h-4 w-4" /></button>
                  <button onClick={() => setDlg({ ...p })} className="text-slate-400 hover:text-brand-blue mr-3" data-testid={`persona-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p)} className="text-slate-400 hover:text-red-500" data-testid={`persona-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {persone.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nessuna persona registrata.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500" data-testid="persona-gio-legenda">
        <span className="font-semibold text-slate-600">Giornaliera:</span>
        <span className="inline-flex items-center gap-1"><span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5"><CalendarDays className="h-3 w-3" />Personale</span> = compila il proprio calendario (account collegato)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 rounded px-1.5 py-0.5"><Users className="h-3 w-3" />Vede Generale</span> = vede/gestisce il calendario di tutti (sono due cose separate)</span>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="persona-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuova"} persona</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="persona-nome" value={dlg.nome} onChange={(e) => setDlg({ ...dlg, nome: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ruolo</Label><Select value={dlg.ruolo} onValueChange={(v) => setDlg({ ...dlg, ruolo: v })}><SelectTrigger data-testid="persona-ruolo"><SelectValue /></SelectTrigger><SelectContent>{RUOLI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Sede</Label><Select value={dlg.sede} onValueChange={(v) => setDlg({ ...dlg, sede: v })}><SelectTrigger data-testid="persona-sede"><SelectValue /></SelectTrigger><SelectContent>{SEDI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>N. RUI</Label><Input value={dlg.rui_number} onChange={(e) => setDlg({ ...dlg, rui_number: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={dlg.telefono} onChange={(e) => setDlg({ ...dlg, telefono: e.target.value })} /></div>
              </div>
              <div><Label>Email</Label><Input value={dlg.email} onChange={(e) => setDlg({ ...dlg, email: e.target.value })} /></div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2"><input type="checkbox" checked={dlg.attivo} onChange={(e) => setDlg({ ...dlg, attivo: e.target.checked })} /><Label>Attivo</Label></div>
                <div className="flex items-center gap-2"><input type="checkbox" data-testid="persona-approver" checked={dlg.is_approver} onChange={(e) => setDlg({ ...dlg, is_approver: e.target.checked })} /><Label>Delegato (può approvare)</Label></div>
                <div className="flex items-center gap-2"><input type="checkbox" data-testid="persona-vede-calendario" checked={!!dlg.vede_calendario} onChange={(e) => setDlg({ ...dlg, vede_calendario: e.target.checked })} /><Label>Vede Calendario Generale</Label></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="persona-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AccountDialog data={accountDlg} onClose={() => setAccountDlg(null)} reload={reload} />

      <Dialog open={!!fascPersona} onOpenChange={(o) => !o && setFascPersona(null)}>
        <DialogContent className="max-w-2xl" data-testid="fascicolo-dialog">
          <DialogHeader><DialogTitle>Fascicolo — {fascPersona?.nome}</DialogTitle></DialogHeader>
          {fascPersona && <Fascicolo personaId={fascPersona.id} canManage={true} />}
          <DialogFooter><Button variant="outline" onClick={() => setFascPersona(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccountDialog({ data, onClose, reload }) {
  const [form, setForm] = useState({ email: "", password: "", is_approver: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm({ email: data.email, password: "", is_approver: data.is_approver }); }, [data]);
  const p = data?.persona;
  const hasAccount = !!p?.user_id;
  const submit = async () => {
    if (!form.email.includes("@")) return toast.error("Email non valida");
    if (form.password.length < 6) return toast.error("Password: minimo 6 caratteri");
    setBusy(true);
    try {
      await api.post(`/personale/persone/${p.id}/account`, form);
      toast.success(hasAccount ? "Accesso aggiornato" : "Accesso creato"); onClose(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revocare l'accesso di questo dipendente?")) return;
    setBusy(true);
    try { await api.delete(`/personale/persone/${p.id}/account`); toast.success("Accesso revocato"); onClose(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="account-dialog">
        <DialogHeader><DialogTitle>Accesso di {p?.nome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-500">{hasAccount ? "Aggiorna email/password o modifica il ruolo. Inserisci una nuova password per resettarla." : "Crea le credenziali di accesso all'area riservata per questo dipendente."}</p>
          <div><Label>Email di accesso</Label><Input type="email" data-testid="account-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@assietica.it" /></div>
          <div><Label>{hasAccount ? "Nuova password" : "Password"}</Label><Input type="text" data-testid="account-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 caratteri" /></div>
          <div className="flex items-center gap-2"><input type="checkbox" data-testid="account-approver" checked={form.is_approver} onChange={(e) => setForm({ ...form, is_approver: e.target.checked })} /><Label>Delegato (può approvare richieste)</Label></div>
        </div>
        <DialogFooter className="gap-2">
          {hasAccount && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 mr-auto" onClick={revoke} disabled={busy} data-testid="account-revoke">Revoca accesso</Button>}
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="account-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : hasAccount ? "Aggiorna" : "Crea accesso"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StipendiTab({ persone, nomeOf }) {
  const [rows, setRows] = useState([]);
  const [mese, setMese] = useState(thisMonth());
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/personale/stipendi", { params: { mese } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [mese]);
  const empty = { persona_id: "", mese, importo: 0, data_bonifico: "", stato: "da_pagare", note: "" };
  const save = async () => {
    if (!dlg.persona_id) return toast.error("Seleziona la persona");
    try {
      if (dlg.id) await api.put(`/personale/stipendi/${dlg.id}`, dlg);
      else await api.post("/personale/stipendi", dlg);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/personale/stipendi/${r.id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2"><Label className="text-sm">Mese</Label><Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="w-40" data-testid="stipendi-mese" /></div>
        <Button data-testid="stipendi-add" onClick={() => setDlg({ ...empty, mese })}><Plus className="h-4 w-4 mr-1.5" />Nuovo stipendio</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Persona</TableHead><TableHead>Importo</TableHead><TableHead>Data bonifico</TableHead><TableHead>Stato</TableHead><TableHead>Busta paga</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`stipendio-row-${r.id}`}>
                <TableCell className="font-medium">{nomeOf(r.persona_id)}</TableCell>
                <TableCell>{euro(r.importo)}</TableCell>
                <TableCell>{r.data_bonifico || "—"}</TableCell>
                <TableCell><span className={r.stato === "pagato" ? "text-green-600 text-xs" : "text-orange-600 text-xs"}>{r.stato === "pagato" ? "Pagato" : "Da pagare"}</span></TableCell>
                <TableCell className="w-48"><FileUpload modulo="busta_paga" refId={r.id} compact testid={`bp-${r.id}`} accept=".pdf,.png,.jpg,.jpeg" /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessuno stipendio per {mese}.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="stipendio-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuovo"} stipendio</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Persona</Label><Select value={dlg.persona_id} onValueChange={(v) => setDlg({ ...dlg, persona_id: v })}><SelectTrigger data-testid="stipendio-persona"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mese</Label><Input type="month" value={dlg.mese} onChange={(e) => setDlg({ ...dlg, mese: e.target.value })} /></div>
                <div><Label>Importo (€)</Label><Input type="number" value={dlg.importo} onChange={(e) => setDlg({ ...dlg, importo: e.target.value })} data-testid="stipendio-importo" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data bonifico</Label><Input type="date" value={dlg.data_bonifico} onChange={(e) => setDlg({ ...dlg, data_bonifico: e.target.value })} /></div>
                <div><Label>Stato</Label><Select value={dlg.stato} onValueChange={(v) => setDlg({ ...dlg, stato: v })}><SelectTrigger data-testid="stipendio-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="da_pagare">Da pagare</SelectItem><SelectItem value="pagato">Pagato</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Note</Label><Textarea value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="stipendio-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TfrTab({ persone, nomeOf }) {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/personale/tfr").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const empty = { persona_id: "", tipo: "TFR", fondo: "Groupama", importo: 0, data_versamento: "", eseguito_da: "", stato: "da_versare", note: "" };
  const save = async () => {
    if (!dlg.persona_id) return toast.error("Seleziona la persona");
    try {
      if (dlg.id) await api.put(`/personale/tfr/${dlg.id}`, dlg);
      else await api.post("/personale/tfr", dlg);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/personale/tfr/${r.id}`); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="tfr-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuovo versamento</Button></div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Persona</TableHead><TableHead>Tipo</TableHead><TableHead>Fondo</TableHead><TableHead>Importo</TableHead><TableHead>Data</TableHead><TableHead>Eseguito da</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`tfr-row-${r.id}`}>
                <TableCell className="font-medium">{nomeOf(r.persona_id)}</TableCell>
                <TableCell>{r.tipo}</TableCell>
                <TableCell>{r.fondo}</TableCell>
                <TableCell>{euro(r.importo)}</TableCell>
                <TableCell>{r.data_versamento || "—"}</TableCell>
                <TableCell>{r.eseguito_da || "—"}</TableCell>
                <TableCell><span className={r.stato === "versato" ? "text-green-600 text-xs" : "text-orange-600 text-xs"}>{r.stato === "versato" ? "Versato" : "Da versare"}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Nessun versamento registrato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="tfr-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuovo"} versamento TFR/PIP</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Persona</Label><Select value={dlg.persona_id} onValueChange={(v) => setDlg({ ...dlg, persona_id: v })}><SelectTrigger data-testid="tfr-persona"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tipo</Label><Select value={dlg.tipo} onValueChange={(v) => setDlg({ ...dlg, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="TFR">TFR</SelectItem><SelectItem value="PIP">PIP</SelectItem></SelectContent></Select></div>
                <div><Label>Fondo</Label><Input value={dlg.fondo} onChange={(e) => setDlg({ ...dlg, fondo: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Importo (€)</Label><Input type="number" value={dlg.importo} onChange={(e) => setDlg({ ...dlg, importo: e.target.value })} data-testid="tfr-importo" /></div>
                <div><Label>Data versamento</Label><Input type="date" value={dlg.data_versamento} onChange={(e) => setDlg({ ...dlg, data_versamento: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Eseguito da</Label><Input value={dlg.eseguito_da} onChange={(e) => setDlg({ ...dlg, eseguito_da: e.target.value })} placeholder="es. Umberto / Mariella" /></div>
                <div><Label>Stato</Label><Select value={dlg.stato} onValueChange={(v) => setDlg({ ...dlg, stato: v })}><SelectTrigger data-testid="tfr-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="da_versare">Da versare</SelectItem><SelectItem value="versato">Versato</SelectItem></SelectContent></Select></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="tfr-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GiornaliereTab({ persone, nomeOf }) {
  const [rows, setRows] = useState([]);
  const [mese, setMese] = useState(thisMonth());
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/personale/giornaliere", { params: { mese } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [mese]);
  const empty = { persona_id: "", mese, ferie_giorni: 0, permessi_ore: 0, spese_importo: 0, stato: "bozza", note: "" };
  const save = async () => {
    if (!dlg.persona_id) return toast.error("Seleziona la persona");
    try {
      if (dlg.id) await api.put(`/personale/giornaliere/${dlg.id}`, dlg);
      else await api.post("/personale/giornaliere", dlg);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/personale/giornaliere/${r.id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2"><Label className="text-sm">Mese</Label><Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="w-40" data-testid="giorn-mese" /></div>
        <Button data-testid="giorn-add" onClick={() => setDlg({ ...empty, mese })}><Plus className="h-4 w-4 mr-1.5" />Nuova giornaliera</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Persona</TableHead><TableHead>Ferie (gg)</TableHead><TableHead>Permessi (h)</TableHead><TableHead>Spese</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`giorn-row-${r.id}`}>
                <TableCell className="font-medium">{nomeOf(r.persona_id)}</TableCell>
                <TableCell>{r.ferie_giorni}</TableCell>
                <TableCell>{r.permessi_ore}</TableCell>
                <TableCell>{euro(r.spese_importo)}</TableCell>
                <TableCell><span className={r.stato === "completata" ? "text-green-600 text-xs" : "text-slate-500 text-xs"}>{r.stato === "completata" ? "Completata" : "Bozza"}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessuna giornaliera per {mese}.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="giorn-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuova"} giornaliera</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Persona</Label><Select value={dlg.persona_id} onValueChange={(v) => setDlg({ ...dlg, persona_id: v })}><SelectTrigger data-testid="giorn-persona"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Mese</Label><Input type="month" value={dlg.mese} onChange={(e) => setDlg({ ...dlg, mese: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Ferie (gg)</Label><Input type="number" value={dlg.ferie_giorni} onChange={(e) => setDlg({ ...dlg, ferie_giorni: e.target.value })} /></div>
                <div><Label>Permessi (h)</Label><Input type="number" value={dlg.permessi_ore} onChange={(e) => setDlg({ ...dlg, permessi_ore: e.target.value })} /></div>
                <div><Label>Spese (€)</Label><Input type="number" value={dlg.spese_importo} onChange={(e) => setDlg({ ...dlg, spese_importo: e.target.value })} /></div>
              </div>
              <div><Label>Stato</Label><Select value={dlg.stato} onValueChange={(v) => setDlg({ ...dlg, stato: v })}><SelectTrigger data-testid="giorn-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bozza">Bozza</SelectItem><SelectItem value="completata">Completata</SelectItem></SelectContent></Select></div>
              <div><Label>Note</Label><Textarea value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="giorn-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
